/* ═══════════════════════════════════════════════════════════════
   DashboardViewService — per-user saved views on any list/grid.

   `surface` is a logical id ('forecasting', 'projects',
   'reports.utilization', etc).  `config` is freeform JSON the surface
   owns.  We don't validate the shape here — the calling page reads
   what it knows about and ignores the rest.

   Personal vs shared:
     - default: personal — only the owner sees it
     - shared = true: anyone in the firm sees it on the same surface
       (gated server-side by dashboards.share_views perm before save)
   ═══════════════════════════════════════════════════════════════ */

import type { DashboardView } from '../../shared/entities';
import * as cache from './cloudCache';

const TABLE = 'dashboard_views';
const opts = { jsonFields: ['config'] };

export const DashboardViewService = {
  /** All views for a surface — own + shared. Caller filters from there. */
  listForSurface: async (surface: string, userId: string): Promise<DashboardView[]> => {
    const all = await cache.list<DashboardView>(TABLE, opts);
    return all.filter((v) => v.surface === surface && (v.userId === userId || v.shared));
  },

  /** Just the user's own views on a surface. */
  listOwnForSurface: async (surface: string, userId: string): Promise<DashboardView[]> => {
    const all = await cache.list<DashboardView>(TABLE, opts);
    return all.filter((v) => v.surface === surface && v.userId === userId);
  },

  /** Returns the user's default view for a surface, or null. */
  getDefault: async (surface: string, userId: string): Promise<DashboardView | null> => {
    const own = await DashboardViewService.listOwnForSurface(surface, userId);
    return own.find((v) => v.isDefault) || null;
  },

  save: async (view: DashboardView): Promise<DashboardView> => cache.save(TABLE, view, opts),

  delete: async (id: string): Promise<void> => cache.remove(TABLE, id),

  /** Mark a view as the user's default for a surface; clears the flag
   *  on any other rows owned by them on the same surface. */
  setDefault: async (viewId: string, surface: string, userId: string): Promise<void> => {
    const own = await DashboardViewService.listOwnForSurface(surface, userId);
    await Promise.all(
      own.map((v) => {
        const wantDefault = v.id === viewId;
        if (!!v.isDefault === wantDefault) return Promise.resolve();
        return DashboardViewService.save({ ...v, isDefault: wantDefault });
      })
    );
  },
};
